import { OptionEvent } from './options.types';


/**
 * i.e Feature is a convert-ps-to-pdf
 */
export interface Feature {
    [featureUrl: string]: ConvertOptionCategories;
}

/**
 * ConvertOptionCategories contains categories that displayed on options
 * pane under the upload button. Every converter should contain at least
 * one ConvertOptionCategoy, which is outputExt that is used to get convert
 * to file type
 */
export interface ConvertOptionCategories {
    formCategories: Array<ConvertOptionCategoy>;
}

/**
 * Each category can have multiple formgroups. They are all displayed on a block
 */
export interface ConvertOptionCategoy {
    category: string;
    categoryLabel: string;
    formGroups: Array<ConvertOption>;
    hiddenFn?: (convertOptionsContext: any) => boolean;
}

export interface ConvertOption {
    formGroupName: string;
    formGroupDisplayName: string;
    shouldReloadOptionFn?: (optionChangeRequest: Event) => boolean;
    hiddenFn?: (optionChangeRequest: Event) => boolean;
    note?: {
        bsTextClass: string,
        text: string,
    };
    selections: DropdownOption[];
}

export type DisplayType = 'leaf' |
    'parent';
export class DisplayTypes {
    public static LEAF: DisplayType = 'leaf';
    public static PARENT: DisplayType = 'parent';
}

export type WidgetType = 'dropdown' |
    'input' |
    'input_dropdown' |
    'switch' |
    'seekbar'|
    'checkbox';
export class WidgetTypes {
    public static DROPDOWN: WidgetType = 'dropdown';
    public static INPUT: WidgetType = 'input';
    public static INPUT_DROPDOWN: WidgetType = 'input_dropdown';
    public static SWITCH: WidgetType = 'switch';
    public static SEEKBAR: WidgetType = 'seekbar';
    public static CHECKBOX: WidgetType = 'checkbox';
}

export interface IDropdownOption {
    /**
     * Name of the property. This provide instructions to the command executor. This is basically
     * a command line argument "key" name.
     */
    formName: string;
    /**
     * Server readable value used as value of key value pair arguments to the executor
     */
    formValue: string;
    /**
     * Human readable value
     */
    formDisplayValue: string;
    selected: boolean;
    /**
     * Instruct the parent component what to do when user select this item from the dropdown.
     * Default to leaf. Leaf is an item that does nothing once selected. If an option is a leaf option on a
     * dropdown, then selecting this item would not render child element to the right of the dropdown.
     * Parent options will cause to render things defined by formChildren on the DOM, next to this dropdown
     */
    displayAs?: DisplayType;

    formChildren?: DropdownOption[];

    featureFlags?: {
        offlineOnly?: {
            state: boolean,
            errorMessage: string;
        };
        canSelect?: {
            fn?: (container: any, iocTypes?: any) => Promise<boolean>,
            errorMessage?: string;
        };
    };
    note?: {
        bsTextClass: string,
        text: string,
    };
}

export class DropdownOption implements IDropdownOption, Note {
    formName: string;
    formValue: string;
    formDisplayValue: string;
    formDisplaySuffix?: string;
    selected: boolean;
    iconPath?: string;
    displayAs?: DisplayType;
    /***
     * types => DropdownOption[] | IInputDropdownWidget[]
     */
    formChildren?: (any | DropdownOption | IInputDropdownWidget)[]; // DropdownOption[] | IInputDropdownWidget[];
    shownFn?: (allOptions: any) => boolean;
    validatorFn?: (value: any, type: any) => boolean | string;
    featureFlags?: {
        offlineOnly?: {
            state: boolean,
            errorMessage: string;
        };
        canSelect?: {
            fn?: (container: any, iocTypes: any) => Promise<boolean>,
            errorMessage?: string;
        };
    };

    note?: NoteData;

    constructor() {
        this.formName = undefined;
        this.formValue = undefined;
        this.formDisplayValue = undefined;
        this.selected = undefined;
        this.displayAs = undefined;
        this.formChildren = undefined;
        this.validatorFn = undefined;
        this.featureFlags = undefined;
        this.note = {
            bsTextClass: undefined,
            text: undefined,
            textFormatter: undefined,
        };
    }
}

export interface IWidget {
    formName: string;
    formDisplayValue: string;
    displayAs: (string | WidgetType | DisplayType);
    /**
     * Server readable value used as value of key value pair arguments to the executor
     */
    formValue: string;
    formValueType?: "string" | "number";
}

export interface IParentWidget<T> extends IWidget {
    formChildren?: DropdownOption[];
    formGroup?: ConvertOption;
    options: T;
    shouldReloadOptionFn?: (changedFormItem: any) => boolean;
    hiddenFn?: (changedFormItem: any) => boolean;
}

export interface ISeekBarWidget {
    start: number;
    end: number;
    value: number;
}

export interface ICheckboxWidget {
    value: boolean;
}

export interface NoteData {
    bsTextClass: string;
    text: string;
    iconColor?: string;
    textFormatter?: (text: string, dataFill: { selectedValue: string } | any, uploadInProgressFiles?: any) => string;
}

export class NoteData {
    bsTextClass: string;
    text: string;
    iconColor?: string;
    textFormatter?: (text: string, dataFill: { selectedValue: string } | any, uploadInProgressFiles?: any) => string;
}

export interface Note {
    note?: NoteData;
}

export class GenericWidget<T> implements IParentWidget<T>, Note {
    formName;
    formDisplayValue;
    formValue;
    displayAs: (string | WidgetType | DisplayType);
    formChildren?: DropdownOption[];
    formGroup?;
    type: WidgetType;
    options: T;
    note?: NoteData;
    shouldReloadOptionFn?: (optionChangeRequest: OptionEvent) => boolean;

    constructor() {
        this.formName = undefined;
        this.formDisplayValue = undefined;
        this.formValue = undefined;
        this.displayAs = undefined;
        this.formChildren = undefined;
        this.formGroup = undefined;
        this.type = undefined;
        this.options = undefined;
        this.note = {
            bsTextClass: undefined,
            text: undefined,
            textFormatter: undefined,
        };
    }
}

export interface IDropdown extends IWidget {
    formChildren?: DropdownOption[];
    formGroup?: ConvertOption;
    selections?: DropdownOption[];
    shouldReloadOptionFn?: (changedFormItem: any) => boolean;
}

export class DropdownWidget implements IDropdown, Note {
    static TYPE = 'dropdown';

    formName;
    formDisplayValue;
    formValue;
    displayAs;
    formChildren: DropdownOption[];
    formGroup;
    type;
    selections: DropdownOption[];
    note?: NoteData;
    shouldReloadOptionFn?: (optionChangeRequest: OptionEvent) => boolean;

    constructor() {
        this.formName = undefined;
        this.formDisplayValue = undefined;
        this.formValue = undefined;
        this.displayAs = undefined;
        this.formChildren = undefined;
        this.formGroup = undefined;
        this.type = undefined;
        this.selections = undefined;
        this.note = undefined;
    }
}

export interface IInputWidget extends IWidget {
    type: string;
    placeholder: string;
    defaultValue: any;
}

export interface INumberInputWidget extends IInputWidget {
    minimum: number;
    maximum: number;
}

export interface IInputDropdownWidget extends INumberInputWidget, IDropdown {
}

export class InputWidget implements IInputWidget {
    static TYPE = 'input';

    formName;
    formDisplayValue;
    formValue;
    displayAs;
    formGroup;
    type;
    placeholder;
    defaultValue;
    minimum;
    maximum;

    constructor() {
        this.formName = undefined;
        this.formDisplayValue = undefined;
        this.formValue = undefined;
        this.displayAs = undefined;
        this.formGroup = undefined;
        this.type = undefined;
        this.placeholder = undefined;
        this.defaultValue = undefined;
        this.minimum = undefined;
        this.maximum = undefined;
    }
}

export class InputDropdownWidget implements IInputDropdownWidget {
    static TYPE = 'input_dropdown';

    formName;
    formDisplayValue;
    formValue;
    displayAs;
    formChildren;
    formGroup;
    type;
    placeholder;
    defaultValue;
    minimum;
    maximum;
    selections;

    constructor() {
        this.formName = undefined;
        this.formDisplayValue = undefined;
        this.formValue = undefined;
        this.displayAs = undefined;
        this.formChildren = undefined;
        this.formGroup = undefined;
        this.type = undefined;
        this.placeholder = undefined;
        this.defaultValue = undefined;
        this.minimum = undefined;
        this.maximum = undefined;
        this.selections = undefined;
    }
}

export interface IToggleWidget {
    type: 'switch';
    placeholder: string;
    defaultValue: any;
}

export interface FormItem {
    formName: string;
    formValue: any;
}
